import { Component, Prop, Event, EventEmitter, State, Element, h } from '@stencil/core';
import { htmlSanitizer } from '../../utils/html-sanitizer';

// Shared CTA interface
interface CTA {
  ctaConversionType?: string;
  ctaButtonColor?: string;
  ctaStyle?: string;
  ctaAnchorLabelRequired?: string;
  ctaNoFollow?: string;
  ctaTitle?: string;
  ctaLinkType?: string;
  ctaLinkTarget?: string;
  ctaLink?: string;
  videoModal?: boolean;
}

export interface Tab {
  id: string;
  label: string;
  headline?: string;
  description?: string;
  imageUrl?: string;
  primaryButtonText?: string;
  linkButtonText?: string;
  imageAltText?: string;
  tabLabel?: string;
  videoModal?: boolean;
  headlineLevel?: string;
  videoLink?: string;
  body?: string;
  ctas?: CTA[];
  videoThumbnailUrl?: string;
  assetType?: 'image' | 'video' | 'ambient-video-loop' | 'ambient-video-static';
  'title-block'?: any;
  backgroundColor: 'yellow' | 'blue' | 'green' | 'brown';
}

export interface VsTextImageData {
  imageAltText?: string;
  imageFormat?: 'cropped' | 'full-bleed' | 'half';
  orientation?: 'left' | 'right';
  videoModal?: boolean;
  mobileAltText?: string;
  tabs?: Tab[];
  assetAspectRatio?: string;
  body?: string;
  ctas?: CTA[];
  assetType?: 'image' | 'video' | 'ambient-video-loop' | 'ambient-video-static';
  showTabs?: boolean;
  imageUrl?: string;
  mobileImagePath?: string;
  headlineLevel?: string;
  layoutType?: string;
  theme?: 'light' | 'dark';
  videoLink?: string;
  headline?: string;
  eyebrowText?: string;
  videoThumbnailUrl?: string;
  backgroundColor: 'yellow' | 'blue' | 'green' | 'brown';
  // Accessibility properties
  ariaLabel?: string;
  tabListLabel?: string;
  // Backward compatibility
  lazyLoad?: boolean;
  videoUrl?: string;
  bottomMargin?: boolean;
  videoPosterUrl?: string;
  description?: string;
  primaryButtonText?: string;
  linkButtonText?: string;
  defaultActiveTab?: string;
}

// Content interface for current state
interface CurrentContent {
  headline: string;
  description: string;
  imageUrl: string;
  primaryButtonText: string;
  linkButtonText: string;
  videoLink?: string;
  titleBlock?: any;
}

@Component({
  tag: 'vs-text-image',
  styleUrl: 'vs-text-image.scss',
  shadow: false,
})
export class VsTextImage {
  @Element() el: HTMLElement;

  // JSON data prop - primary data source
  @Prop() data?: string | VsTextImageData;

  // Individual properties (backward compatibility)
  @Prop() imageAltText?: string;
  @Prop() imageFormat?: 'cropped' | 'full-bleed' | 'half' = 'cropped';
  @Prop() orientation?: 'left' | 'right' = 'left';
  @Prop() videoModal?: boolean = false;
  @Prop() tabs?: Tab[] | string = [];
  @Prop() assetAspectRatio?: string = '16:9';
  @Prop() lazyLoad?: boolean = true;
  @Prop() assetType?: 'image' | 'video' = 'image';
  @Prop() videoUrl?: string;
  @Prop() bottomMargin?: boolean = false;
  @Prop() showTabs?: boolean = true;
  @Prop() imageUrl?: string;
  @Prop() theme?: 'light' | 'dark' = 'light';
  @Prop() videoPosterUrl?: string;
  @Prop() defaultActiveTab?: string;
  @Prop() headline?: string;
  @Prop() description?: string;
  @Prop() primaryButtonText?: string;
  @Prop() linkButtonText?: string;

  @State() eyebrowText: string = '';
  @State() activeTabId: string = '';
  @State() previousTabId: string = '';
  @State() currentContent: CurrentContent;
  @State() parsedTabs: Tab[] = [];
  @State() isTransitioning: boolean = false;
  @State() isInViewport: boolean = false;
  @State() animationDirection: 'left-to-right' | 'right-to-left' = 'right-to-left';

  @Event() tabChange: EventEmitter<string>;
  @Event() primaryClick: EventEmitter<void>;
  @Event() linkClick: EventEmitter<void>;

  private intersectionObserver?: IntersectionObserver;
  private parsedData: VsTextImageData | null = null;

  componentWillLoad() {
    this.initializeComponent();
  }

  componentDidLoad() {
    // this.setupViewportObserver();
  }

  disconnectedCallback() {
    this.cleanupViewportObserver();
  }

  // Initialize component state
  private initializeComponent() {
    this.parsedData = this.parseData();
    this.parsedTabs = this.parseTabs();
    this.activeTabId = this.getDefaultActiveTab() || this.parsedTabs?.[0]?.id || '';
    this.updateCurrentContent();
  }

  // Parse JSON data once and cache it
  private parseData(): VsTextImageData | null {
    if (!this.data) return null;

    if (typeof this.data === 'string') {
      try {
        return JSON.parse(this.data);
      } catch (e) {
        console.error('Failed to parse data JSON:', e);
        return null;
      }
    }
    return this.data;
  }

  // Generic data value getter with fallback chain
  private getDataValue<K extends keyof VsTextImageData>(
    key: K,
    fallback?: any
  ): VsTextImageData[K] {
    return this.parsedData?.[key] ?? (this as any)[key] ?? fallback;
  }

  // Parse tabs with optimized mapping
  private parseTabs(): Tab[] {
    const tabsFromData = this.parsedData?.tabs;

    // Priority: JSON data tabs > string tabs > array tabs
    if (tabsFromData?.length) {
      return tabsFromData.map((tab, index) => this.mapTabData(tab, index));
    }

    if (typeof this.tabs === 'string') {
      try {
        const parsedTabs = JSON.parse(this.tabs);
        return parsedTabs?.length
          ? parsedTabs.map((tab, index) => this.mapTabData(tab, index))
          : [];
      } catch (e) {
        console.error('Failed to parse tabs JSON:', e);
        return [];
      }
    }

    return Array.isArray(this.tabs) ? this.tabs : [];
  }

  // Map tab data with consistent structure
  private mapTabData(tab: any, index: number): Tab {
    return {
      id: tab.id || `tab-${index}`,
      label: tab.tabLabel || tab.label || `Tab ${index + 1}`,
      headline: tab.headline,
      description: tab.body || tab.description,
      imageUrl: tab.imageUrl,
      imageAltText: tab.imageAltText,
      videoModal: tab.videoModal,
      headlineLevel: tab.headlineLevel,
      videoLink: tab.videoLink,
      primaryButtonText: this.extractCtaTitle(tab.ctas, 0) || tab.primaryButtonText,
      linkButtonText: this.extractCtaTitle(tab.ctas, 1) || tab.linkButtonText,
      ctas: tab.ctas,
      videoThumbnailUrl: tab.videoThumbnailUrl,
      assetType: tab.assetType,
      'title-block': tab['title-block'],
      backgroundColor: tab.backgroundColor,
    };
  }

  // Extract CTA title helper
  private extractCtaTitle(ctas: CTA[] | undefined, index: number): string | undefined {
    const title = ctas?.[index]?.ctaTitle;
    return title && title.trim() !== '' ? title : undefined;
  }

  // Consolidated content getters
  private getDefaultActiveTab(): string {
    return this.getDataValue('defaultActiveTab', '');
  }
  private getImageFormat(): 'cropped' | 'full-bleed' | 'half' {
    return this.getDataValue('imageFormat', 'cropped');
  }
  private getOrientation(): 'left' | 'right' {
    return this.getDataValue('orientation', 'left');
  }

  private getBackgroundColor(): 'yellow' | 'blue' | 'green' | 'brown' {
    return this.getDataValue('backgroundColor');
  }

  private getImageUrl(): string {
    return this.getDataValue('imageUrl');
  }
  private getHeadline(): string {
    return this.getDataValue('headline');
  }
  private getImageAltText(): string {
    return this.getDataValue('imageAltText');
  }

  private getVideoLink(): string {
    return this.getDataValue('videoLink');
  }
  private getAssetType(): 'image' | 'video' | 'ambient-video-loop' | 'ambient-video-static' {
    // If we have a video link, default to 'video' type even if assetType is not explicitly set
    const videoLink = this.getVideoLink()?.trim();
    if (videoLink) {
      return this.getDataValue('assetType', 'video');
    }
    return this.getDataValue('assetType', 'image');
  }

  private getVideoType(): 'video-loop' | 'video-single' {
    const activeTab = this.parsedTabs?.find(tab => tab.id === this.activeTabId);
    const hasTabsData = this.parsedTabs?.length > 0;

    // Get asset type from active tab if available, otherwise from main data
    const assetType =
      hasTabsData && activeTab?.assetType ? activeTab.assetType : this.getAssetType();

    // Map asset types to vsr-video's expected videoType values
    switch (assetType) {
      case 'ambient-video-loop':
        return 'video-loop';
      case 'video':
      case 'ambient-video-static':
      default:
        return 'video-single';
    }
  }

  private getTheme(): 'light' | 'dark' {
    return this.getDataValue('theme', 'light');
  }

  private getDescription(): string {
    return this.parsedData?.body || this.getDataValue('description');
  }

  private getPrimaryButtonText(): string {
    const ctaTitle = this.extractCtaTitle(this.parsedData?.ctas, 0);
    return ctaTitle || this.getDataValue('primaryButtonText');
  }

  private getLinkButtonText(): string {
    const ctaTitle = this.extractCtaTitle(this.parsedData?.ctas, 1);
    return ctaTitle || this.getDataValue('linkButtonText');
  }

  private getEyebrowText() {
    return this.eyebrowText;
  }

  private removeEyebrowTextFromTitleBlock(
    titleBlockData: Record<string, any>,
    imageFormat: string
  ): Record<string, any> {
    const newObj: Record<string, any> = {};
    for (const key of Object.keys(titleBlockData)) {
      if (key === 'eyebrowText' && imageFormat != 'half') {
        this.eyebrowText = titleBlockData[key];
        break;
      }
      newObj[key] = titleBlockData[key];
    }
    return newObj;
  }

  // Update current content based on active tab
  private updateCurrentContent() {
    const activeTab = this.parsedTabs?.find(tab => tab.id === this.activeTabId);
    const hasTabsData = this.parsedTabs?.length > 0;
    const videoLink = hasTabsData
      ? activeTab?.videoLink || this.getVideoLink()
      : this.getVideoLink();

    this.currentContent = {
      headline: hasTabsData ? activeTab?.headline || this.getHeadline() : this.getHeadline(),
      description: hasTabsData
        ? activeTab?.description || this.getDescription()
        : this.getDescription(),
      imageUrl: hasTabsData ? activeTab?.imageUrl || this.getImageUrl() : this.getImageUrl(),
      primaryButtonText: hasTabsData
        ? activeTab?.primaryButtonText || this.getPrimaryButtonText()
        : this.getPrimaryButtonText(),
      linkButtonText: hasTabsData
        ? activeTab?.linkButtonText || this.getLinkButtonText()
        : this.getLinkButtonText(),
      titleBlock: hasTabsData
        ? activeTab?.['title-block']
        : this.parsedTabs?.length > 0
          ? this.removeEyebrowTextFromTitleBlock(
              this.parsedTabs?.[0]?.['title-block'],
              this.getImageFormat()
            )
          : this.removeEyebrowTextFromTitleBlock(
              this.parsedData?.['title-block'],
              this.getImageFormat()
            ),
      ...(videoLink?.trim() && { videoLink }),
    };
  }

  // Get previous content for animation
  private getPreviousContent() {
    if (!this.previousTabId) return null;

    const previousTab = this.parsedTabs?.find(tab => tab.id === this.previousTabId);
    if (!previousTab) return null;

    const hasTabsData = this.parsedTabs?.length > 0;
    const videoLink = hasTabsData
      ? previousTab?.videoLink || this.getVideoLink()
      : this.getVideoLink();

    return {
      headline: hasTabsData ? previousTab?.headline || this.getHeadline() : this.getHeadline(),
      description: hasTabsData
        ? previousTab?.description || this.getDescription()
        : this.getDescription(),
      imageUrl: hasTabsData ? previousTab?.imageUrl || this.getImageUrl() : this.getImageUrl(),
      primaryButtonText: hasTabsData
        ? previousTab?.primaryButtonText || this.getPrimaryButtonText()
        : this.getPrimaryButtonText(),
      linkButtonText: hasTabsData
        ? previousTab?.linkButtonText || this.getLinkButtonText()
        : this.getLinkButtonText(),
      ...(videoLink?.trim() && { videoLink }),
    };
  }

  // Helper methods for media type detection
  private getPreviousVideoLink(): string | null {
    if (!this.previousTabId) return null;

    const previousTab = this.parsedTabs?.find(tab => tab.id === this.previousTabId);
    const hasTabsData = this.parsedTabs?.length > 0;

    if (hasTabsData && previousTab) {
      // If we have tabs and a previous tab, only use video if the tab's asset type supports it
      const tabAssetType = previousTab.assetType;
      if (
        tabAssetType &&
        (tabAssetType.includes('video') ||
          tabAssetType === 'ambient-video-loop' ||
          tabAssetType === 'ambient-video-static')
      ) {
        return previousTab.videoLink?.trim() || null;
      }
      // If tab asset type is 'image', don't show video regardless of fallbacks
      return null;
    }

    // If no tabs, use main component video link
    return this.getVideoLink()?.trim() || null;
  }

  private shouldRenderAsVideo(content: any, tabId?: string): boolean {
    if (!content) return false;

    // Check if there's a video link
    const hasVideoLink = content.videoLink?.trim();
    if (!hasVideoLink) return false;

    // If we have a specific tab ID, check that tab's asset type
    if (tabId) {
      const tab = this.parsedTabs?.find(t => t.id === tabId);
      if (tab?.assetType) {
        return (
          tab.assetType.includes('video') ||
          tab.assetType === 'ambient-video-loop' ||
          tab.assetType === 'ambient-video-static'
        );
      }
    }

    // Fallback to main component asset type
    const assetType = this.getAssetType();
    return (
      assetType.includes('video') ||
      assetType === 'ambient-video-loop' ||
      assetType === 'ambient-video-static'
    );
  }

  private renderPreviousMediaElement() {
    const previousContent = this.getPreviousContent();
    if (!previousContent) return null;

    const previousVideoLink = this.getPreviousVideoLink();
    const shouldShowVideo = this.shouldRenderAsVideo(previousContent, this.previousTabId);

    if (shouldShowVideo && previousVideoLink) {
      // Render previous video
      const previousTab = this.parsedTabs?.find(tab => tab.id === this.previousTabId);
      const videoThumbnail = previousTab?.videoThumbnailUrl || previousContent.imageUrl;
      const zIndex = this.animationDirection === 'right-to-left' ? '2' : '1';

      return (
        <div
          class={{
            'visa-text-image-media-element': true,
            previous: true,
            'slide-out': this.animationDirection === 'right-to-left',
            'slide-left': this.animationDirection === 'right-to-left',
          }}
          style={{
            position: 'absolute',
            top: '0',
            left: '0',
            width: '100%',
            height: '100%',
            zIndex,
          }}
        >
          {this.renderVideoElement(previousVideoLink, videoThumbnail, '', true)}
          <div class="image-mask"></div>
        </div>
      );
    } else if (previousContent.imageUrl) {
      // Render previous image
      return (
        <img
          src={previousContent.imageUrl}
          alt=""
          aria-hidden="true"
          class={{
            'visa-text-image-media-element': true,
            previous: true,
            'slide-out': this.animationDirection === 'right-to-left',
            'slide-left': this.animationDirection === 'right-to-left',
          }}
          style={{ zIndex: this.animationDirection === 'right-to-left' ? '2' : '1' }}
        >
          <div class="image-mask"></div>
        </img>
      );
    }

    return null;
  }

  private renderCurrentMediaElement() {
    const currentVideoLink = this.getCurrentVideoLink();
    const shouldShowVideo = this.shouldRenderAsVideo(this.currentContent, this.activeTabId);
    const imageUrl = this.currentContent?.imageUrl || this.getImageUrl();

    // Get image alt text from active tab if available, otherwise from main data
    const activeTab = this.parsedTabs?.find(tab => tab.id === this.activeTabId);
    const hasTabsData = this.parsedTabs?.length > 0;
    const imageAlt =
      hasTabsData && activeTab?.imageAltText ? activeTab.imageAltText : this.getImageAltText();

    if (shouldShowVideo && currentVideoLink) {
      // Render current video
      const videoThumbnail =
        hasTabsData && activeTab?.videoThumbnailUrl
          ? activeTab.videoThumbnailUrl
          : this.getDataValue('videoThumbnailUrl') || imageUrl;

      const zIndex = this.animationDirection === 'left-to-right' ? '2' : '1';

      return (
        <div
          class={{
            'visa-text-image-media-element': true,
            current: true,
            'slide-in': this.isTransitioning && this.animationDirection === 'left-to-right',
            'slide-right': this.animationDirection === 'left-to-right',
          }}
          style={{
            position: 'absolute',
            top: '0',
            left: '0',
            width: '100%',
            height: '100%',
            zIndex,
          }}
        >
          {this.renderVideoElement(currentVideoLink, videoThumbnail, '', false)}
          {this.isTransitioning && <div class="image-mask"></div>}
        </div>
      );
    } else if (imageUrl) {
      // Render current image
      return (
        <img
          src={imageUrl}
          alt={imageAlt === 'NA' ? '' : imageAlt || ''}
          class={{
            'visa-text-image-media-element': true,
            current: true,
            'slide-in': this.isTransitioning && this.animationDirection === 'left-to-right',
            'slide-right': this.animationDirection === 'left-to-right',
          }}
          style={{ zIndex: this.animationDirection === 'left-to-right' ? '2' : '1' }}
        >
          {this.isTransitioning && <div class="image-mask"></div>}
        </img>
      );
    }

    return null;
  }

  // Video-related methods
  // private hasVideoLink(): boolean {
  //   return (
  //     this.parsedTabs.some(tab => tab.videoLink?.trim()) || Boolean(this.getVideoLink()?.trim())
  //   );
  // }

  private getCurrentVideoLink(): string | null {
    const activeTab = this.parsedTabs?.find(tab => tab.id === this.activeTabId);
    const hasTabsData = this.parsedTabs?.length > 0;

    if (hasTabsData && activeTab) {
      // If we have tabs and an active tab, only use video if the tab's asset type supports it
      const tabAssetType = activeTab.assetType;
      if (
        tabAssetType &&
        (tabAssetType.includes('video') ||
          tabAssetType === 'ambient-video-loop' ||
          tabAssetType === 'ambient-video-static')
      ) {
        return activeTab.videoLink?.trim() || null;
      }
      // If tab asset type is 'image', don't show video regardless of fallbacks
      return null;
    }

    // If no tabs, use main component video link
    return this.getVideoLink()?.trim() || null;
  }

  private renderVideoElement(
    videoSrc: string,
    posterSrc?: string,
    _additionalClasses?: string,
    isPrevious?: boolean
  ) {
    const activeTab = this.parsedTabs?.find(tab => tab.id === this.activeTabId);
    const hasTabsData = this.parsedTabs?.length > 0;
    // Get asset type from active tab if available, otherwise from main data
    const assetType =
      hasTabsData && activeTab?.assetType ? activeTab.assetType : this.getAssetType();

    const videoType = assetType === 'ambient-video-loop' ? 'video-loop' : 'video-single';

    // Video element rendering - only autoplay if not a previous video and component is in viewport
    const shouldAutoplay = this.isInViewport && !isPrevious;

    return (
      <div class="visa-text-image-video-wrapper">
        <vsr-video
          src={videoSrc}
          poster={posterSrc}
          video-type={videoType}
          autoplay={shouldAutoplay}
          muted={true}
          controls={true}
          preload="auto"
        ></vsr-video>
      </div>
    );
  }

  // private setupViewportObserver() {
  //   // Clean up any existing observer first
  //   this.cleanupViewportObserver();

  //   // Create a new IntersectionObserver
  //   this.intersectionObserver = new IntersectionObserver(
  //     entries => {
  //       // Update isInViewport state based on intersection
  //       const isVisible = entries.some(entry => entry.isIntersecting);
  //       if (isVisible !== this.isInViewport) {
  //         this.isInViewport = isVisible;

  //         // Force re-render when visibility changes to trigger autoplay on vsr-video components
  //         if (isVisible) {
  //           // Update state to trigger re-render with autoplay=true
  //           this.isInViewport = true;

  //           // For any remaining native video elements
  //           const videos = this.el.querySelectorAll('video');
  //           videos.forEach(video => {
  //             if (video) {
  //               video.play().catch(console.warn);
  //             }
  //           });

  //           // For vsr-video components, set autoplay attribute
  //           const vsrVideos = this.el.querySelectorAll('vsr-video');
  //           vsrVideos.forEach(vsrVideo => {
  //             if (vsrVideo) {
  //               vsrVideo.setAttribute('autoplay', 'true');
  //               // Try to access the internal video element and play it directly
  //               const internalVideo = vsrVideo.querySelector('video');
  //               if (internalVideo) {
  //                 internalVideo.play().catch(console.warn);
  //               }
  //             }
  //           });
  //         }
  //       }
  //     },
  //     {
  //       root: null, // Use viewport as root
  //       rootMargin: '0px',
  //       threshold: 0.1, // Trigger when at least 10% of the element is visible
  //     }
  //   );

  //   // Start observing the component element
  //   this.intersectionObserver.observe(this.el);
  // }

  private cleanupViewportObserver() {
    this.intersectionObserver?.disconnect();
    this.intersectionObserver = undefined;
  }

  // Event handlers
  private handleTabChange = async (newTabId: string) => {
    if (newTabId === this.activeTabId || this.isTransitioning) return;

    this.isTransitioning = true;

    const contentElements = this.el?.shadowRoot?.querySelectorAll('.content-transition');
    const currentIndex = this.parsedTabs.findIndex(t => t.id === this.activeTabId);
    const newIndex = this.parsedTabs.findIndex(t => t.id === newTabId);
    this.animationDirection = newIndex > currentIndex ? 'right-to-left' : 'left-to-right';

    this.previousTabId = this.activeTabId;
    const tabIndicator = this.el?.shadowRoot?.querySelector(
      '.visa-text-image-tab-indicator'
    ) as HTMLElement | null;

    tabIndicator?.classList.add('animating');
    contentElements?.forEach(el => el.classList.add('fade-out'));
    await new Promise(r => setTimeout(r, 100));
    this.activeTabId = newTabId;
    this.updateCurrentContent();
    this.tabChange.emit(newTabId);
    const nextBtn = this.el?.shadowRoot?.querySelector(
      `#tab-${newTabId}`
    ) as HTMLButtonElement | null;

    nextBtn?.focus();
    contentElements?.forEach(el => {
      el.classList.remove('fade-out');
      el.classList.add('fade-in');
    });
    const animationTime = this.getImageFormat() === 'full-bleed' ? 400 : 600;

    // Handle CTA animations for new tab content
    setTimeout(() => {
      this.handleNewTabCtaAnimations();
    }, 150);

    // For full-bleed format, we need to completely replace the video element
    // No need to force update here, as we're using a unique key in renderMediaContent
    // which will cause React/JSX to recreate the element when the key changes

    setTimeout(() => {
      contentElements?.forEach(el => el.classList.remove('fade-in'));
      tabIndicator?.classList.remove('animating');
      this.isTransitioning = false;
    }, animationTime);
  };

  // Handle CTA animations for new tab content
  private handleNewTabCtaAnimations() {
    // Find CTA containers within this component that need animation
    const ctaContainers = this.el.querySelectorAll('.vsr-title-button-group.blockReveal');

    ctaContainers.forEach(container => {
      // Only animate if it doesn't already have the scroll-down class
      if (
        !container.classList.contains('scroll-down') &&
        !container.getAttribute('data-animated')
      ) {
        // Check if the container is in viewport (similar to scroll-animation.js logic)
        const rect = container.getBoundingClientRect();
        const isInViewport = rect.top < window.innerHeight && rect.bottom > 0;

        if (isInViewport) {
          // Manually add the animation classes
          container.classList.add('scroll-down');
          container.setAttribute('data-animated', 'true');
        }
      }
    });
  }

  private handlePrimaryClick = () => {
    const activeTab = this.parsedTabs?.find(tab => tab.id === this.activeTabId);
    const hasTabsData = this.parsedTabs?.length > 0;

    // Get CTA from active tab if available, otherwise from main data
    const cta =
      hasTabsData && activeTab?.ctas?.[0] ? activeTab.ctas[0] : this.parsedData?.ctas?.[0];

    if (cta?.ctaLink) {
      this.handleCtaClick(cta);
    }

    this.primaryClick.emit();
  };

  private handleLinkClick = (event?: CustomEvent<MouseEvent>) => {
    const activeTab = this.parsedTabs?.find(tab => tab.id === this.activeTabId);
    const hasTabsData = this.parsedTabs?.length > 0;

    const cta =
      hasTabsData && activeTab?.ctas?.[1] ? activeTab.ctas[1] : this.parsedData?.ctas?.[1];

    if (cta?.ctaLink) {
      this.handleCtaClick(cta);
    }

    if (event?.detail) {
      event.detail.preventDefault();
    }

    this.linkClick.emit();
  };

  private handleCtaClick = (cta: CTA) => {
    if (!cta.ctaLink) return;

    const url = cta.ctaLink;
    const target = cta.ctaLinkTarget;

    if (target === '_blank' || target === 'new') {
      // Open in new tab/window
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      // Open in same tab (default behavior)
      window.location.href = url;
    }
  };

  private handleTabKeyDown = (event: KeyboardEvent) => {
    const tabs = this.parsedTabs;
    if (!tabs.length) return;

    const currentIndex = tabs.findIndex(t => t.id === this.activeTabId);
    if (currentIndex === -1) return;

    const last = tabs.length - 1;
    let nextIdx = currentIndex;

    switch (event.key) {
      case 'ArrowRight':
      case 'Right': // legacy
        nextIdx = (currentIndex + 1) % tabs.length;
        break;

      case 'ArrowLeft':
      case 'Left':
        nextIdx = (currentIndex - 1 + tabs.length) % tabs.length;
        break;

      case 'Home':
        nextIdx = 0;
        break;

      case 'End':
        nextIdx = last;
        break;

      default:
        return; // do nothing for other keys
    }

    event.preventDefault();
    this.handleTabChange(tabs[nextIdx].id);
  };

  private renderTabNavigation(tabs: Tab[], activeTab: string) {
    // Find the active tab index for the sliding indicator
    const activeTabIndex = tabs.findIndex(tab => tab.id === activeTab);

    return (
      <nav
        class={`visa-text-image-tab-nav`}
        role="tablist"
        aria-label={this.getDataValue('tabListLabel', 'Content tabs')}
      >
        {tabs.map((tab, index) => (
          <button
            key={tab.id}
            onClick={() => this.handleTabChange(tab.id)}
            class={`analytics-button visa-text-image-tab-button ${activeTab === tab.id ? 'visa-text-image-active' : ''} ${tab.backgroundColor}`}
            role="tab"
            id={`tab-${tab.id}`}
            aria-selected={activeTab === tab.id ? 'true' : 'false'}
            aria-controls={`panel-${tab.id}`}
            tabindex={activeTab === tab.id ? 0 : -1}
            onKeyDown={this.handleTabKeyDown}
            data-index={index}
          >
            <span class="visa-text-image-tab-label">{tab.label}</span>
          </button>
        ))}
        <div
          class="visa-text-image-tab-indicator"
          style={{
            transform: `translateX(calc(${activeTabIndex * 100}% + 0.5rem*${activeTabIndex + 1} + 4px)) translateY(-50%)`,
            width: `calc((100% - 1rem - ${tabs.length}*8px)/${tabs.length})`,
          }}
        ></div>
      </nav>
    );
  }

  private renderLinkIcon(cta: CTA | undefined) {
    if (!cta) return null;

    if (cta.ctaLinkTarget === '_blank') {
      return <i class="icon icon-external-link" aria-hidden="true"></i>;
    } else if (cta.ctaLinkType === 'external') {
      return <i class="icon icon-external-link" aria-hidden="true"></i>;
    } else if (cta.ctaLinkType === 'modal') {
      return <i class="icon icon-message-square" aria-hidden="true"></i>;
    } else if (this.videoModal && cta.videoModal) {
      return <i class="icon icon-video" aria-hidden="true"></i>;
    }

    return null;
  }

  private renderButtonGroup(primaryText?: string, linkText?: string) {
    const showPrimary = primaryText?.trim();
    const showLink = linkText?.trim();

    const activeTab = this.parsedTabs?.find(tab => tab.id === this.activeTabId);
    const hasTabsData = this.parsedTabs?.length > 0;

    // Get CTAs from active tab if available, otherwise from main data
    const primaryCta =
      hasTabsData && activeTab?.ctas?.[0] ? activeTab.ctas[0] : this.parsedData?.ctas?.[0];
    const linkCta =
      hasTabsData && activeTab?.ctas?.[1] ? activeTab.ctas[1] : this.parsedData?.ctas?.[1];

    if (!showPrimary && !showLink) return null;

    return (
      <div class="visa-text-image-button-group">
        {showPrimary &&
          (this.getImageFormat() === 'full-bleed' ? (
            <vsr-button-v2
              variant="secondary"
              href={primaryCta?.ctaLink || '#'}
              themeOverride={this.getTheme() == 'light' ? 'dark' : 'light'}
              state={primaryCta?.ctaLink ? 'active' : 'inactive'}
              onVsrClick={this.handlePrimaryClick}
            >
              <span innerHTML={htmlSanitizer(primaryText)}></span>
              {this.renderLinkIcon(primaryCta)}
            </vsr-button-v2>
          ) : (
            <vsr-link-button-v2
              variant="primary"
              href={primaryCta?.ctaLink || '#'}
              themeOverride={this.getTheme()}
              state={primaryCta?.ctaLink ? 'active' : 'inactive'}
              onVsrClick={this.handlePrimaryClick}
            >
              <span innerHTML={htmlSanitizer(primaryText)}></span>
              {this.renderLinkIcon(primaryCta)}
            </vsr-link-button-v2>
          ))}
        {showLink && (
          <vsr-link-button-v2
            themeOverride={this.getTheme()}
            state={linkCta?.ctaLink ? 'active' : 'inactive'}
            href={linkCta?.ctaLink || '#'}
            onVsrClick={this.handleLinkClick}
          >
            <span innerHTML={htmlSanitizer(linkText)}></span>
            {this.renderLinkIcon(linkCta)}
          </vsr-link-button-v2>
        )}
      </div>
    );
  }

  private renderContentBlock(content: CurrentContent, isFullbleed = false) {
    return (
      <section
        class={`visa-text-image-content-block ${isFullbleed ? 'visa-text-image-fullbleed' : ''}`}
        role="tabpanel"
        id={`panel-${this.activeTabId}`}
        // aria-labelledby={this.activeTabId ? `tab-${this.activeTabId}` : content.titleBlock.headline}
        aria-labelledby={`tab-${this.activeTabId}`}
        tabIndex={0}
      >
        {content.titleBlock && (
          <vsr-title
            title-block={JSON.stringify(content.titleBlock)}
            is-bg-from-parent={true}
            theme={this.parsedData.theme || this.theme}
          ></vsr-title>
        )}
        <div class={isFullbleed ? 'visa-text-image-fullbleed-content' : ''}>
          {this.renderButtonGroup(content.primaryButtonText, content.linkButtonText)}
        </div>
      </section>
    );
  }

  private renderMediaContent() {
    const activeTab = this.parsedTabs?.find(tab => tab.id === this.activeTabId);
    const hasTabsData = this.parsedTabs?.length > 0;
    const videoLink = this.getCurrentVideoLink();
    const imageUrl = this.currentContent?.imageUrl || this.getImageUrl();

    // Get image alt text from active tab if available, otherwise from main data
    const imageAlt =
      hasTabsData && activeTab?.imageAltText ? activeTab.imageAltText : this.getImageAltText();

    const imageFormat = this.getImageFormat();
    const isCropped = imageFormat === 'cropped';
    const isFullBleed = imageFormat === 'full-bleed';

    // For cropped layout with transitions (supports mixed media)
    if (isCropped && this.isTransitioning) {
      return [
        // Previous media element (could be image or video)
        this.renderPreviousMediaElement(),
        // Current media element (could be image or video)
        this.renderCurrentMediaElement(),
      ].filter(Boolean);
    }
    // For non-transitioning content
    else if (videoLink) {
      // Render video without transition
      const videoThumbnail =
        hasTabsData && activeTab?.videoThumbnailUrl
          ? activeTab.videoThumbnailUrl
          : this.getDataValue('videoThumbnailUrl') || imageUrl;

      const videoKey = isFullBleed ? `video-${this.activeTabId}` : undefined;

      return (
        <div class="visa-text-image-video-wrapper" style={{ width: '100%', height: '100%' }}>
          <vsr-video
            key={videoKey}
            src={videoLink}
            poster={videoThumbnail}
            video-type={this.getVideoType()}
            autoplay={true}
            muted={true}
            controls={true}
          ></vsr-video>
        </div>
      );
    } else if (imageUrl) {
      // Render image without transition
      return (
        <img
          src={imageUrl}
          alt={imageAlt === 'NA' ? '' : imageAlt || ''}
          class="visa-text-image-media-element"
        />
      );
    }

    return null;
  }

  private renderEyebrowText(eyebrowText: string, isFullbleed = false) {
    return eyebrowText ? (
      <div
        class={`visa-text-image-eyebrow-text ${isFullbleed ? 'visa-text-image-fullbleed-content visa-text-image-full-bleed-content-eyebrow' : ''}`}
      >
        {eyebrowText}
      </div>
    ) : null;
  }

  private renderMedia() {
    const videoLink = this.getCurrentVideoLink();
    if (videoLink) {
      // Render video in half format
      return (
        <div class="vs-text-image-video-container">
          <vsr-video
            src={videoLink}
            poster={
              this.getDataValue('videoThumbnailUrl') ||
              this.currentContent.imageUrl ||
              this.getImageUrl()
            }
            video-type={this.getVideoType()}
            autoplay={true}
            muted={true}
            controls={true}
          ></vsr-video>
        </div>
      );
    }
    return (
      <div
        class="vs-text-image-image-container"
        style={{
          backgroundImage:
            this.currentContent.imageUrl || this.getImageUrl()
              ? `url(${this.currentContent.imageUrl || this.getImageUrl()})`
              : 'none',
        }}
        role="img"
        aria-label={this.getImageAltText}
      ></div>
    );
  }

  render() {
    const imageFormat = this.getImageFormat();
    const isSplitLayout = imageFormat === 'cropped';
    const isRightAligned = this.getOrientation() === 'left';
    const backGroundColor = this.getBackgroundColor();
    const hasTabsData = this.parsedTabs?.length > 0;
    const eyebrowText = this.getEyebrowText();

    const theme = this.getTheme();

    const containerClass = [
      `vs-text-image-container text-image`,
      `vs-text-image-container-${this.parsedData.theme || this.theme}`,
      `media-${this.getOrientation()}`,
    ].join(' ');

    const content = (
      <div class="vs-text-image-content-wrapper">
        <div class="vs-text-image-content-wrapper-title">
          <vsr-title
            title-block={JSON.stringify(this.currentContent?.titleBlock)}
            is-bg-from-parent={false}
            theme={this.parsedData.theme || this.theme}
          ></vsr-title>
        </div>
      </div>
    );

    const media = this.renderMedia();

    return (
      <div class={`visa-text-image visa-text-image-theme-${theme}`}>
        {isSplitLayout && (
          <div
            class={{
              'visa-text-image-split-layout': true,
              [backGroundColor]: !hasTabsData,
              'visa-text-image-reversed': isRightAligned,
              transitioning: this.isTransitioning,
              'left-to-right': this.animationDirection === 'left-to-right',
              'right-to-left': this.animationDirection === 'right-to-left',
            }}
          >
            {/* Mobile tab navigation */}
            <div class="visa-text-image-tab-nav-mobile">
              {hasTabsData && this.renderTabNavigation(this.parsedTabs, this.activeTabId)}
            </div>

            {/* Media container */}
            <div class="visa-text-image-media-container">
              <div class="visa-text-image-media-wrapper cardFlipReveal">
                {this.renderMediaContent()}
              </div>
            </div>

            {/* Content container */}
            <div
              class={`visa-text-image-content-container visa-text-image-aligned-start`}
              data-animation-container
            >
              {/* Mobile eyebrow */}
              <div class="visa-text-image-eyebrow-mobile">
                {!hasTabsData && this.renderEyebrowText(eyebrowText)}
              </div>

              {/* Desktop tab navigation or eyebrow */}
              <div class="visa-text-image-tab-nav-desktop">
                {hasTabsData
                  ? this.renderTabNavigation(this.parsedTabs, this.activeTabId)
                  : this.renderEyebrowText(eyebrowText)}
              </div>

              {this.renderContentBlock(this.currentContent)}
            </div>
          </div>
        )}

        {imageFormat === 'full-bleed' && (
          <div class={`visa-text-image-fullbleed-layout`}>
            {/* Background media */}
            <div class="visa-text-image-fullbleed-media">
              <div class="visa-text-image-media-wrapper">{this.renderMediaContent()}</div>
            </div>
            <div class="visa-text-image-fullbleed-overlay"></div>

            {/* Content overlay */}
            <div
              class={`visa-text-image-fullbleed-content-wrapper ${isRightAligned ? 'visa-text-image-reversed' : ''}`}
            >
              <div class="visa-text-image-fullbleed-spacer"></div>
              <div class="visa-text-image-fullbleed-content-area">
                <div class="visa-text-image-content-wrapper visa-text-image-content-transition">
                  <div class="visa-text-image-fullbleed-nav-area">
                    {hasTabsData
                      ? this.renderTabNavigation(this.parsedTabs, this.activeTabId)
                      : this.renderEyebrowText(eyebrowText, true)}
                  </div>
                  <div class="visa-text-image-fullbleed-content visa-text-image-content-transition">
                    {this.renderContentBlock(this.currentContent, true)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        {imageFormat == 'half' && (
          <div class={containerClass}>
            {this.getOrientation() === 'left' ? media : content}
            {this.getOrientation() === 'left' ? content : media}
          </div>
        )}
      </div>
    );
  }
}
